/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { type FC, lazy } from "react";
import {
	BrowserRouter,
	Navigate,
	Route,
	Routes,
	useNavigate,
} from "react-router-dom";
import { LayoutSplashScreen } from "../../_metronic/layout/core";
import ErrorBoundary from "../../_metronic/layout/core/ErrorBoundary";
import { App } from "../App";
import { AuthPage, Logout, useAuth } from "../modules/auth";
import * as authHelper from "../modules/auth/core/AuthHelpers";
import { useTokenRefresher } from "../modules/auth/core/useTokenRefresher";
import { Error404 } from "../modules/errors/components/Error404";
import { ErrorsLayout } from "../modules/errors/ErrorsLayout";
import { ErrorsPage } from "../modules/errors/ErrorsPage";
import { setNavigator } from "./navigationService";
import { PrivateRoutes } from "./PrivateRoutes";

const ContactUs = lazy(() => import("../modules/auth/components/ContactAs"));
const CertificateVerifyPage = lazy(
	() => import("../pages/training/CertificateVerifyPage"),
);
const { BASE_URL } = import.meta.env;

const AppRoutes: FC = () => {
	const { currentUser } = useAuth();

	useTokenRefresher();

	const auth = authHelper.getAuth();
	const isWaitingForUser = auth?.access_token && !currentUser;

	return (
		<BrowserRouter basename={BASE_URL}>
			<ErrorBoundary>
				<NavigatorSetter />
				<Routes>
					<Route element={<App />}>
						{/* Public routes */}
						<Route path="error/*" element={<ErrorsPage />} />
						<Route path="logout" element={<Logout />} />
						<Route path="verify/:token" element={<CertificateVerifyPage />} />

						{isWaitingForUser ? (
							<Route path="*" element={<LayoutSplashScreen />} />
						) : (
							<>
								<Route path="auth/*" element={<AuthPage />} />

								{currentUser &&
								currentUser?.role_id != null &&
								currentUser?.role_id !== "" ? (
									<>
										<Route path="/*" element={<PrivateRoutes />} />
										<Route
											index
											element={<Navigate to="/dashboard" replace />}
										/>
									</>
								) : (
									<>
										<Route path="*" element={<Navigate to="/auth" replace />} />
										<Route index element={<Navigate to="/auth" replace />} />
									</>
								)}

								{/* 404 - Only if we have no other match and auth is ready */}
								<Route element={<ErrorsLayout />}>
									<Route path="*" element={<Error404 />} />
								</Route>
							</>
						)}
					</Route>
				</Routes>
			</ErrorBoundary>
		</BrowserRouter>
	);
};

function NavigatorSetter() {
	const navigate = useNavigate();
	setNavigator(navigate);
	return null;
}

export { AppRoutes };
